import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Progress, Typography, List, Avatar, Badge, Alert, message } from 'antd';
import {
  UserOutlined,
  FileTextOutlined,
  CloudServerOutlined,
  SafetyOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  WarningOutlined
} from '@ant-design/icons';
import apiClient from '../../services/api';

const { Title, Text } = Typography;

const SystemMonitor = () => {
  const [loading, setLoading] = useState(true);
  const [systemStats, setSystemStats] = useState({
    totalUsers: 0,
    totalNotes: 0,
    activeUsers: 0,
    totalKnowledgeBases: 0
  });

  const [systemHealth, setSystemHealth] = useState({
    cpu: 0,
    memory: 0,
    disk: 0,
    network: 0
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [systemAlerts, setSystemAlerts] = useState([]);

  // 获取系统统计数据
  const fetchSystemStats = async () => {
    try {
      const response = await apiClient.get('/admin/system/stats');
      setSystemStats(response.data);
    } catch (error) {
      console.error('获取系统统计数据失败:', error);
      message.error('Failed to fetch system statistics');
    }
  };

  // 获取系统健康状态
  const fetchSystemHealth = async () => {
    try {
      const response = await apiClient.get('/admin/system/health');
      setSystemHealth(response.data);
    } catch (error) {
      console.error('获取系统健康状态失败:', error);
      // 如果API不存在，使用默认值
      setSystemHealth({
        cpu: Math.floor(Math.random() * 30) + 40,
        memory: Math.floor(Math.random() * 20) + 60,
        disk: Math.floor(Math.random() * 15) + 70,
        network: Math.floor(Math.random() * 25) + 30
      });
    }
  };

  // 获取最近活动
  const fetchRecentActivities = async () => {
    try {
      const response = await apiClient.get('/admin/system/activities');
      setRecentActivities(response.data);
    } catch (error) {
      console.error('获取最近活动失败:', error);
      // 使用默认活动数据
      setRecentActivities([
        {
          id: 1,
          type: 'user',
          message: 'System startup complete',
          time: new Date().toLocaleString('en-US'),
          status: 'success'
        }
      ]);
    }
  };

  // 获取系统警告
  const fetchSystemAlerts = async () => {
    try {
      const response = await apiClient.get('/admin/system/alerts');
      setSystemAlerts(response.data);
    } catch (error) {
      console.error('获取系统警告失败:', error);
      setSystemAlerts([]);
    }
  };

  // 初始化数据
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      await Promise.all([
        fetchSystemStats(),
        fetchSystemHealth(),
        fetchRecentActivities(),
        fetchSystemAlerts()
      ]);
      setLoading(false);
    };

    initializeData();

    // 设置定时刷新
    const interval = setInterval(() => {
      fetchSystemHealth();
      fetchRecentActivities();
    }, 30000); // 每30秒刷新一次

    return () => clearInterval(interval);
  }, []);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'user':
        return <UserOutlined style={{ color: '#52c41a' }} />;
      case 'note':
        return <FileTextOutlined style={{ color: '#1890ff' }} />;
      case 'system':
        return <CloudServerOutlined style={{ color: '#722ed1' }} />;
      case 'warning':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      default:
        return <UserOutlined />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return '#52c41a';
      case 'warning':
        return '#faad14';
      case 'error':
        return '#f5222d';
      default:
        return '#1890ff';
    }
  };

  const getHealthColor = (value) => {
    if (value > 80) return '#f5222d';
    if (value > 60) return '#faad14';
    return '#52c41a';
  };

  return (
    <div className="admin-module">
      <div className="admin-module-header">
        <Title level={3} className="admin-module-title">System Monitor Overview</Title>
        <Text className="admin-module-description">
          Real-time monitoring of system status and key metrics
        </Text>
      </div>

      {/* 系统警告 */}
      {systemAlerts.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          {systemAlerts.map(alert => (
            <Alert
              key={alert.id}
              message={alert.message}
              type={alert.type}
              showIcon
              closable
              style={{ marginBottom: 8 }}
            />
          ))}
        </div>
      )}

      {/* 核心统计数据 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="Total Users"
              value={systemStats.totalUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="Total Notes"
              value={systemStats.totalNotes}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="Active Users"
              value={systemStats.activeUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="Knowledge Bases"
              value={systemStats.totalKnowledgeBases}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 详细监控信息 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="System Resource Usage" loading={loading}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text>CPU Usage</Text>
                <Text>{systemHealth.cpu}%</Text>
              </div>
              <Progress 
                percent={systemHealth.cpu} 
                strokeColor={getHealthColor(systemHealth.cpu)} 
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text>Memory Usage</Text>
                <Text>{systemHealth.memory}%</Text>
              </div>
              <Progress 
                percent={systemHealth.memory} 
                strokeColor={getHealthColor(systemHealth.memory)} 
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text>Disk Usage</Text>
                <Text>{systemHealth.disk}%</Text>
              </div>
              <Progress 
                percent={systemHealth.disk} 
                strokeColor={getHealthColor(systemHealth.disk)} 
              />
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text>Network Traffic</Text>
                <Text>{systemHealth.network}%</Text>
              </div>
              <Progress 
                percent={systemHealth.network} 
                strokeColor={getHealthColor(systemHealth.network)} 
              />
            </div>
          </Card>
        </Col>
        
        <Col xs={24} lg={12}>
          <Card title="Recent Activity" loading={loading}>
            <List
              itemLayout="horizontal"
              dataSource={recentActivities}
              renderItem={item => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <Avatar icon={getActivityIcon(item.type)} style={{ backgroundColor: getStatusColor(item.status) }} />
                    }
                    title={item.message}
                    description={item.time}
                  />
                  <Badge status={item.status} />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SystemMonitor; 